// 📄 components/Configuracion/InterfazZFormModal.tsx
// Modal de alta Y edición de una interfaz/canal Z en Configuración >
// Desarrollo Z del proyecto (ver ConfiguracionInterfacesZ.tsx) — mismo
// patrón que TransaccionZFormModal.tsx / BadiZFormModal.tsx: un solo modal,
// `interfazInicial` presente = modo edición. Dirección relativa a la
// plataforma elegida, mismo criterio que InterfaceFormModal.tsx (catálogo
// global). "Guardar" persiste DIRECTO contra Firestore (interfazZService.ts).
import { useState } from "react";
import { X, Save, RefreshCw } from "lucide-react";
import { Timestamp } from "firebase/firestore";
import { useAuth } from "../../context/AuthContext.tsx";
import {
  InterfazZ, InterfazZData, crearInterfazZ, actualizarInterfazZ
} from "../../services/interfazZService.ts";
import { SistemaAmbiente, SISTEMAS_AMBIENTE } from "../../services/transaccionZService.ts";
import {
  ObjetoNegocio, SolucionInterfaz, PlataformaInterfaz, DireccionInterfaz,
  OBJETOS_NEGOCIO, SOLUCIONES_INTERFAZ, PLATAFORMAS_INTERFAZ, DIRECCIONES_INTERFAZ,
  TECNOLOGIAS_INTERFAZ, MIDDLEWARES_INTERFAZ, plataformaInterfazMeta
} from "../../services/catalogoInterfacesService.ts";

interface InterfazZFormModalProps {
  projectId: string;
  // Presente = modo edición; ausente = alta.
  interfazInicial?: InterfazZ | null;
  onGuardado: () => void;
  onCancelar: () => void;
}

const timestampAInputDate = (ts: Timestamp | null | undefined): string => {
  if (!ts) return "";
  return ts.toDate().toISOString().slice(0, 10);
};

export default function InterfazZFormModal({ projectId, interfazInicial, onGuardado, onCancelar }: InterfazZFormModalProps) {
  const { user } = useAuth();
  const modoEdicion = !!interfazInicial;

  const [objetoNegocio, setObjetoNegocio] = useState<ObjetoNegocio | "">(interfazInicial?.objetoNegocio || "");
  const [solucion, setSolucion] = useState<SolucionInterfaz | "">(interfazInicial?.solucion || "");
  const [plataforma, setPlataforma] = useState<PlataformaInterfaz | "">(interfazInicial?.plataforma || "erp");
  const [direccion, setDireccion] = useState<DireccionInterfaz | "">(interfazInicial?.direccion || "ingreso");
  const [textoBreve, setTextoBreve] = useState(interfazInicial?.textoBreve || "");
  const [nombreTecnico, setNombreTecnico] = useState(interfazInicial?.nombreTecnico || "");
  const [transaccion, setTransaccion] = useState(interfazInicial?.transaccion || "");
  const [campoBusqueda, setCampoBusqueda] = useState(interfazInicial?.campoBusqueda || "");
  const [tecnologia, setTecnologia] = useState(interfazInicial?.tecnologia || "");
  const [middleware, setMiddleware] = useState(interfazInicial?.middleware || "");
  const [palabrasClaveInput, setPalabrasClaveInput] = useState(interfazInicial?.palabrasClave?.join(", ") || "");
  const [sistemasDisponibles, setSistemasDisponibles] = useState<SistemaAmbiente[]>(interfazInicial?.sistemasDisponibles || []);
  const [fechaImplementacion, setFechaImplementacion] = useState(timestampAInputDate(interfazInicial?.fechaImplementacion));
  const [responsable, setResponsable] = useState(interfazInicial?.responsable || "");
  const [descripcion, setDescripcion] = useState(interfazInicial?.descripcion || "");
  const [notas, setNotas] = useState(interfazInicial?.notas || "");

  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleSistema = (s: SistemaAmbiente) => {
    setSistemasDisponibles((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
  };

  const nombrePlataforma = plataforma ? plataformaInterfazMeta(plataforma).label : "";

  const handleGuardar = async () => {
    setError(null);
    if (!objetoNegocio) { setError("Selecciona un objeto de negocio."); return; }
    if (!solucion) { setError("Selecciona una solución."); return; }
    if (!plataforma) { setError("Selecciona una plataforma."); return; }
    if (!direccion) { setError("Selecciona una dirección."); return; }
    if (!textoBreve.trim()) { setError("El texto breve es obligatorio."); return; }
    if (!nombreTecnico.trim()) { setError("El nombre técnico / valor a buscar es obligatorio."); return; }

    const payload: InterfazZData = {
      objetoNegocio,
      solucion,
      plataforma,
      transaccion: transaccion.trim(),
      direccion,
      textoBreve: textoBreve.trim(),
      nombreTecnico: nombreTecnico.trim(),
      campoBusqueda: campoBusqueda.trim(),
      tecnologia,
      middleware,
      palabrasClave: palabrasClaveInput.split(",").map((p) => p.trim()).filter(Boolean),
      descripcion: descripcion.trim(),
      responsable: responsable.trim(),
      sistemasDisponibles,
      fechaImplementacion: fechaImplementacion ? Timestamp.fromDate(new Date(`${fechaImplementacion}T00:00:00`)) : null,
      notas: notas.trim()
    };

    setGuardando(true);
    try {
      if (modoEdicion) {
        await actualizarInterfazZ(projectId, interfazInicial!.id, payload);
      } else {
        await crearInterfazZ(projectId, payload, user?.email || user?.uid || "");
      }
      onGuardado();
    } catch (err: any) {
      setError(err.message || "Error al guardar la interfaz.");
    } finally {
      setGuardando(false);
    }
  };

  const intentarCerrar = () => { if (!guardando) onCancelar(); };

  const inputClass = "w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500";
  const labelClass = "block text-xs font-medium text-gray-400 mb-1";

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-[rgb(var(--c-surface))] border border-white/10 rounded-2xl w-full max-w-2xl shadow-2xl max-h-[90vh] flex flex-col my-8">
        <div className="flex items-center justify-between p-5 border-b border-white/10 flex-shrink-0">
          <h3 className="text-lg font-medium text-white">{modoEdicion ? "Editar interfaz Z" : "Nueva interfaz Z"}</h3>
          <button onClick={intentarCerrar} disabled={guardando} className="text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>}

          {/* Fila 1: Objeto de negocio | Solución */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Objeto de negocio*</label>
              <select className={inputClass} value={objetoNegocio} onChange={(e) => setObjetoNegocio(e.target.value as ObjetoNegocio)}>
                <option value="">Selecciona un objeto</option>
                {OBJETOS_NEGOCIO.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>Solución*</label>
              <select className={inputClass} value={solucion} onChange={(e) => setSolucion(e.target.value as SolucionInterfaz)}>
                <option value="">Selecciona una solución</option>
                {SOLUCIONES_INTERFAZ.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </div>
          </div>

          {/* Fila 2: Plataforma | Dirección (dinámica) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Plataforma*</label>
              <select className={inputClass} value={plataforma} onChange={(e) => setPlataforma(e.target.value as PlataformaInterfaz)}>
                {PLATAFORMAS_INTERFAZ.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>Dirección (respecto a {nombrePlataforma})*</label>
              <select className={inputClass} value={direccion} onChange={(e) => setDireccion(e.target.value as DireccionInterfaz)}>
                {DIRECCIONES_INTERFAZ.map((d) => <option key={d.id} value={d.id}>{d.opcion(nombrePlataforma)}</option>)}
              </select>
            </div>
          </div>

          {/* Fila 3: Texto breve */}
          <div>
            <label className={labelClass}>Texto breve*</label>
            <input type="text" className={inputClass} value={textoBreve} onChange={(e) => setTextoBreve(e.target.value)} placeholder="ej. Interfaz de ingreso de facturas recibidas desde Ariba al ERP" />
          </div>

          {/* Fila 4: Nombre técnico / Valor a buscar */}
          <div>
            <label className={labelClass}>Nombre técnico / Valor a buscar*</label>
            <input type="text" className={`${inputClass} font-mono`} value={nombreTecnico} onChange={(e) => setNombreTecnico(e.target.value)} placeholder="ej. ZARBCIG_INVOIC" />
          </div>

          {/* Fila 5: Transacción | Campo de búsqueda */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Transacción</label>
              <input type="text" className={`${inputClass} font-mono`} value={transaccion} onChange={(e) => setTransaccion(e.target.value)} placeholder="ej. WE02" />
            </div>
            <div>
              <label className={labelClass}>Campo de búsqueda</label>
              <input type="text" className={inputClass} value={campoBusqueda} onChange={(e) => setCampoBusqueda(e.target.value)} placeholder="ej. Basic Type" />
            </div>
          </div>

          {/* Fila 6: Tecnología | Middleware */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Tecnología</label>
              <select className={inputClass} value={tecnologia} onChange={(e) => setTecnologia(e.target.value)}>
                <option value="">Sin especificar</option>
                {TECNOLOGIAS_INTERFAZ.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>Middleware</label>
              <select className={inputClass} value={middleware} onChange={(e) => setMiddleware(e.target.value)}>
                <option value="">Sin especificar</option>
                {MIDDLEWARES_INTERFAZ.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          </div>

          {/* Fila 7: Palabras clave */}
          <div>
            <label className={labelClass}>Palabras clave (separadas por coma)</label>
            <input type="text" className={inputClass} value={palabrasClaveInput} onChange={(e) => setPalabrasClaveInput(e.target.value)} placeholder="ej. factura, invoice, INVOIC" />
          </div>

          {/* Fila 8: Sistemas disponibles | Fecha de implementación */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Sistemas disponibles</label>
              <div className="flex items-center gap-4 pt-1">
                {SISTEMAS_AMBIENTE.map((s) => (
                  <label key={s} className="flex items-center gap-1.5 text-xs text-gray-300 cursor-pointer">
                    <input type="checkbox" className="accent-blue-500 w-3.5 h-3.5" checked={sistemasDisponibles.includes(s)} onChange={() => toggleSistema(s)} />
                    {s}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className={labelClass}>Fecha de implementación</label>
              <input type="date" className={inputClass} value={fechaImplementacion} onChange={(e) => setFechaImplementacion(e.target.value)} />
            </div>
          </div>

          {/* Fila 9: Responsable */}
          <div>
            <label className={labelClass}>Responsable</label>
            <input type="text" className={inputClass} value={responsable} onChange={(e) => setResponsable(e.target.value)} placeholder="Nombre del desarrollador/consultor responsable" />
          </div>

          {/* Fila 10: Descripción */}
          <div>
            <label className={labelClass}>Descripción</label>
            <textarea className={`${inputClass} resize-y`} rows={2} value={descripcion} onChange={(e) => setDescripcion(e.target.value)} placeholder="Detalle extendido del flujo..." />
          </div>

          {/* Fila 11: Notas técnicas */}
          <div>
            <label className={labelClass}>Notas técnicas</label>
            <textarea className={`${inputClass} resize-y`} rows={2} value={notas} onChange={(e) => setNotas(e.target.value)} placeholder="Observaciones técnicas adicionales..." />
          </div>
        </div>

        <div className="flex justify-end gap-2 p-5 border-t border-white/10 flex-shrink-0">
          <button type="button" onClick={intentarCerrar} disabled={guardando} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-white/5 disabled:opacity-50 disabled:cursor-not-allowed transition-all">
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleGuardar}
            disabled={guardando}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all"
          >
            {guardando ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {guardando ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}
